import { CrudService } from '@ml/shared/services/crud.service';
import { OrderLocation } from '@ml/orders/types/classes';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { UpdateTaskPosition } from '@ml/orders/types/update-task-position';
import { Task } from '@ml/tasks/types/classes';
import { UpdateResourcePosition } from '@ml/shared/types/interfaces';

@Injectable({
  providedIn: 'root'
})
export class OrderLocationsService extends CrudService<OrderLocation> {
  private orderId: number;

  private removed = new Subject<number[]>();

  constructor(protected http: HttpClient) {
    super(http, false);
  }

  get url(): string {
    return `orders/${this.orderId}/locations`;
  }

  getValues$() {
    return this.values$.asObservable();
  }

  getValues() {
    return this.values$.getValue();
  }

  subscribe$(orderId: number): Observable<OrderLocation[]> {
    this.orderId = orderId;
    return super.list();
  }

  updatePositions(orderId: number, positions: UpdateResourcePosition[]) {
    const url = `orders/${orderId}/locations-positions`;
    return this.http.patch<OrderLocation[]>(url, positions).pipe(
      tap(values => {
        if (this.orderId === orderId && this.values$.value) {
          this.values$.next(values);
        }
      })
    );
  }

  bulkCreate(orderLocations: OrderLocation[]) {
    const url = `${this.url}/create`;
    return this.http.post<OrderLocation[]>(url, orderLocations).pipe(
      tap((values: OrderLocation[]) => {
        this.values$.next([...this.values$.value, ...values]);
      })
    );
  }

  bulkRemove(orderLocations: number[]) {
    const url = `${this.url}/remove`;
    return this.http.post(url, orderLocations).pipe(
      tap(() => {
        this.removed.next(orderLocations);
        const values = this.values$.value
          .filter(value => orderLocations.indexOf(value.id) < 0)
          .map((value, index) => {
            value.position = index;
            return value;
          });
        this.values$.next(values);
      })
    );
  }

  bulkUpdate(orderId: number, locations: number[]) {
    const url = `orders/${orderId}/locations/update`;
    return this.http.post(url, locations);
  }

  delete(valueId: number): Observable<OrderLocation[]> {
    const url = `${this.url}/${valueId}`;
    return this.http.delete<OrderLocation[]>(url).pipe(
      tap(values => this.values$.next(values)),
      tap(() => this.removed.next([valueId]))
    );
  }

  public removed$() {
    return this.removed.asObservable();
  }
}
